import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ChevronRight, ExternalLink, LifeBuoy } from 'lucide-react';
import { IconButton, List, ListRow, Popover, Tooltip, cx, focusRing } from '@/components/ui';
import IosPwaGuideModal from '@/components/IosPwaGuideModal';
import { useUiScale } from '@/contexts/DeviceModeContext';
import { BP_XL_DESIGN_PX, type UiScaleState } from '@/config/uiScale';
import { useSurface } from '@/contexts/SurfaceContext';
import { EDGE_DOCK_CLASS } from '@/layouts/edgeDock';
import { playClickSound } from '@/utils/soundUtils';
import { SupportLinkTile, useSupportLinks, type SupportLink } from '@/features/help/supportLinks';

type Layout = 'compact' | 'wide';

/** Full-screen flows whose main action the strip would cover, on every layout. */
const HIDDEN_ROUTES = ['/play/', '/account/finance/payment-checkout', '/account/wallet/deposit-checkout'];
/**
 * Guided money steps on phones: the step content and its buttons use the whole width, and the
 * handle would sit on top of them. On wide layouts the strip is beside the centred page.
 */
const HIDDEN_ROUTES_COMPACT = [
  '/money/add',
  '/money/cash-out',
  '/account/finance/wallet/deposit',
  '/account/wallet/payout-request',
  // Redirects to /account/wallet/payout-request (router.tsx); listed so the handle never flashes.
  '/account/finance/wallet/withdraw',
  '/account/finance/deposit',
];

/** True where the strip is not shown (see HIDDEN_ROUTES / HIDDEN_ROUTES_COMPACT). */
export function sideStripHiddenOn(pathname: string, layout: Layout): boolean {
  if (HIDDEN_ROUTES.some((path) => pathname.startsWith(path))) return true;
  return layout === 'compact' && HIDDEN_ROUTES_COMPACT.some((path) => pathname.startsWith(path));
}

/**
 * The icon strip needs this much design width (`@bp-xl`, a container breakpoint so JS and CSS
 * agree): below it the strip (and its card) sits on the page's content. Narrower wide layouts
 * get the handle.
 */
export const SIDE_STRIP_MIN_DESIGN_WIDTH = BP_XL_DESIGN_PX;

/**
 * Icon strip or one handle. The strip's words are hover labels, so it is for a desktop-class
 * wide layout at least SIDE_STRIP_MIN_DESIGN_WIDTH wide; tablets (touch: no hover), compact and
 * narrow wide layouts get the handle, whose list shows every word.
 */
export function sideStripStyle(ui: Pick<UiScaleState, 'device' | 'layout' | 'designWidth'>): 'strip' | 'handle' {
  return ui.device === 'desktop' && ui.layout === 'wide' && ui.designWidth >= SIDE_STRIP_MIN_DESIGN_WIDTH
    ? 'strip'
    : 'handle';
}

const stripClass = 'pointer-events-auto m-0 flex list-none flex-col gap-1 rounded-l-container border-y border-l k-card bg-card p-1.5';
const handleClass =
  'pointer-events-auto inline-flex size-12 items-center justify-center rounded-l-container border-y border-l border-edge bg-raised text-primary shadow-raised transition-colors duration-(--k-dur-fast) hover:bg-hover [&>svg]:size-5';

const rowChevron = <ChevronRight aria-hidden className="size-5 text-tertiary" />;
const rowExternal = <ExternalLink aria-hidden className="size-5 text-tertiary" />;

/**
 * Right-edge strip (spec 2026-10-08-kiosk-web-split §5), mounted once in App for guests and
 * signed-in players, only where `can('sideStrip')` (never on kiosks or an unknown surface):
 * - wide desktop layouts of at least SIDE_STRIP_MIN_DESIGN_WIDTH design px: icon buttons (48 design
 *   px) on a card at the right edge, mid-height, each with a hover / focus label;
 * - everything else (phones, portrait, tablets, narrow wide windows): one handle that opens the
 *   same items as a labelled list (sideStripStyle).
 * Items come from useSupportLinks('strip'). It shares the edge dock with the pending-payments
 * floater (src/layouts/edgeDock.ts) and sits below dialogs.
 */
export default function SideStrip() {
  const { t } = useTranslation();
  const { can } = useSurface();
  if (!can('sideStrip')) return null;
  return <SideStripDock label={t('sideStrip.label')} />;
}

/** Split out so the hooks below run only where the strip is allowed. */
function SideStripDock({ label }: { label: string }) {
  const ui = useUiScale();
  const { pathname } = useLocation();
  const { links, iosGuide } = useSupportLinks('strip');
  if (sideStripHiddenOn(pathname, ui.layout)) return null;

  return (
    <>
      <div className={EDGE_DOCK_CLASS.right} data-side-strip="">
        {sideStripStyle(ui) === 'strip' ? <WideStrip label={label} links={links} /> : <CompactHandle label={label} links={links} />}
      </div>
      <IosPwaGuideModal isOpen={iosGuide.open} onClose={iosGuide.onClose} />
    </>
  );
}

function WideStrip({ label, links }: { label: string; links: SupportLink[] }) {
  return (
    <nav aria-label={label}>
      <ul className={stripClass}>
        {links.map((link) => (
          <li key={link.id}>
            <Tooltip label={link.label} side="left">
              <IconButton label={link.label} icon={link.icon} size="xl" variant="ghost" onClick={link.onSelect} />
            </Tooltip>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function CompactHandle({ label, links }: { label: string; links: SupportLink[] }) {
  return (
    <Popover
      aria-label={label}
      align="end"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={label}
          onClick={() => {
            playClickSound();
            props.onClick();
          }}
          className={cx(handleClass, focusRing)}
        >
          <LifeBuoy aria-hidden />
        </button>
      )}
    >
      {(close) => (
        <div className="cosmos-scroll-y min-h-0 p-2">
          <List>
            {links.map((link) => (
              <ListRow
                key={link.id}
                wrap
                onClick={() => {
                  playClickSound();
                  close();
                  link.onSelect();
                }}
                leading={<SupportLinkTile>{link.icon}</SupportLinkTile>}
                title={link.label}
                subtitle={link.hint}
                trailing={link.external ? rowExternal : rowChevron}
              />
            ))}
          </List>
        </div>
      )}
    </Popover>
  );
}
